import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { Container } from "react-bootstrap";

import {
  emptyString,
  processedReportGraphHeaders,
  processedMarketingAttributionReduxConstants,
  chartStyleOptions,
  apexChartStyleOptions,
  MARKETING_ATTRIBUTION_LABELS,
  PAGINATION_INDEXING,
  PAGINATION_PAGE_SIZES,
  EMPTY_ARRAY_LENGTH,
} from "../../constants";
import { storesConstants } from "../../../admin-screens/constants";

import { storeActions } from "../../../admin-screens/redux/actions";
import {
  groupsActions,
  processedMarketingAttributionReportActions,
} from "../../redux/actions";
import {
  useSpinner,
  Spinner,
  isGroupAdmin,
  isSuperOrBrandAdmin,
  convertDateToMonthDayYearFormat,
  getMarketingAttributionReportBodyModal,
} from "../../../../shared";
import FiltersComponent from "../marketingAttributionReport/filtersComponent";
import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";
import MarketingAttributionChart from "../marketingAttributionReport/components/marketingAttributionChart";
import MarketingAttributionReportTable from "../marketingAttributionReport/components/marketingAttributionReportTable";
import {
  processMarketingAttributionReportColumns,
  processedDatePreferenceUtility,
} from "./utility";
import {
  buildMarketingAttributionRequestBody,
  transformMarketingAttributionChartData,
} from "../marketingAttributionReport/utility";

const { DAYS_AFTER_MARKETING, TOTAL_ORDERS } = processedReportGraphHeaders;
const { FETCH_ALL_BRANDS, FETCH_BRAND_LOCATIONS } = storesConstants;
const {
  GET_PROCESSED_MARKETING_ATTRIBUTION_GRAPH_DATA,
  GET_PROCESSED_MARKETING_ATTRIBUTION_PAGINATED_DATA,
} = processedMarketingAttributionReduxConstants;

const chartOptions = { ...chartStyleOptions, ...apexChartStyleOptions };

const ProcessedMarketingAttributionReport = (props) => {
  const {
    groupsReducer: { groups },
    processedMarketingAttributionReportReducer: {
      processedMarketingAttributionReportGraphData,
      processedMarketingAttributionReportPagination,
      processedMarketingAttributionReportPaginatedData,
    },
    authenticationReducer: { user },
    storesReducer: { allBrands, stores },
    getAllBrands,
    getGroupsByBrandId,
    getStoresAgainstGroupId,
    getStoresAgainstBrandId,
    getProcessedMarketingAttributionReportGraphData,
    getProcessedMarketingAttributionReportPaginatedData,
  } = props;

  const { brandId, groupId } = useParams();

  const firstUpdate = useRef(true);

  const [selectedBrand, setSelectedBrand] = useState({});
  const [selectedGroup, setSelectedGroup] = useState({
    id: emptyString,
    name: MARKETING_ATTRIBUTION_LABELS.SELECT_GROUP,
  });
  const [selectedLocation, setSelectedLocation] = useState({
    id: emptyString,
    name: MARKETING_ATTRIBUTION_LABELS.SELECT_STORE,
  });
  const [dateState, setDateState] = useState(processedDatePreferenceUtility);
  const [sorting, setSorting] = useState({ id: "deliveryDate", desc: true });
  const [displayName, setDisplayName] = useState(emptyString);
  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const setMarketingAttributionReportBodyModal = useCallback(
    (original) => {
      setComponent(
        getMarketingAttributionReportBodyModal({
          deliveryId: original.deliveryId,
          taskQueueId: original.taskQueueId,
          onClose: closeModal,
        })
      );
    },
    [setComponent, closeModal]
  );

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Check if spinner is running
  const isSpinnerRunning = () =>
    spinner(FETCH_ALL_BRANDS) ||
    spinner(FETCH_BRAND_LOCATIONS) ||
    spinner(GET_PROCESSED_MARKETING_ATTRIBUTION_GRAPH_DATA) ||
    spinner(GET_PROCESSED_MARKETING_ATTRIBUTION_PAGINATED_DATA);

  //Maping Chart Data
  const chartData = useMemo(
    () =>
      transformMarketingAttributionChartData(
        processedMarketingAttributionReportGraphData,
        DAYS_AFTER_MARKETING,
        TOTAL_ORDERS
      ),
    [processedMarketingAttributionReportGraphData]
  );

  const tableColumns = useMemo(
    () =>
      processMarketingAttributionReportColumns(
        setMarketingAttributionReportBodyModal
      ),
    [setMarketingAttributionReportBodyModal]
  );

  //Request body for post request
  const getRequestBody = (
    gId,
    pageNum = PAGINATION_INDEXING.FIRST_PAGE_INDEX
  ) =>
    buildMarketingAttributionRequestBody({
      pageNumber: pageNum,
      pageSize: PAGINATION_PAGE_SIZES.DEFAULT,
      groupId: gId,
      brandId: brandId,
      storeId: selectedLocation.id,
      toTime: dateState?.endDate,
      fromTime: dateState?.startDate,
    });

  //Get Paginated data on page change
  const getDataOnPageChange = (pageNum) => {
    const requestBody = getRequestBody(
      isGroupAdmin() ? groupId : selectedGroup.id,
      pageNum
    );

    getProcessedMarketingAttributionReportPaginatedData(requestBody);
  };

  useEffect(() => {
    if (selectedLocation?.id) setDisplayName(selectedLocation.name);
    else if (selectedGroup?.id) setDisplayName(selectedGroup.name);
    else setDisplayName(selectedBrand?.name || emptyString);
  }, [selectedLocation, selectedGroup, selectedBrand]);

  //Get All brand, and get store and group against current brandId
  useEffect(() => {
    getAllBrands(true);
    getGroupsByBrandId(brandId || user?.brandId);
    if (!isGroupAdmin()) {
      getStoresAgainstBrandId(brandId || user?.brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Get data when group admin login
  useEffect(() => {
    /*In case of group admin skip first time render
    beacause we auto select group and selected group update so it will render again */
    if (firstUpdate.current && isGroupAdmin()) {
      firstUpdate.current = false;
      return;
    }
    const currentGroupId = isSuperOrBrandAdmin() ? selectedGroup.id : groupId;
    const requestBody = getRequestBody(
      currentGroupId,
      PAGINATION_INDEXING.FIRST_PAGE_INDEX
    );
    getProcessedMarketingAttributionReportPaginatedData(requestBody);
    getProcessedMarketingAttributionReportGraphData(requestBody);

    //Reset state on component
    return () => {
      firstUpdate.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, selectedLocation, dateState]);

  //Select brand when brands fetched from backend
  useEffect(() => {
    if (allBrands.length > EMPTY_ARRAY_LENGTH) {
      const currentBrand = allBrands.find(
        (b) => b.id === Number(brandId || user?.brandId)
      );
      setSelectedBrand(currentBrand);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBrands]);

  //If user select group then get store against select group id
  useEffect(() => {
    if (selectedGroup.id && isGroupAdmin()) {
      getStoresAgainstGroupId(selectedGroup.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  //Auto select group if the login user is group admin
  useEffect(() => {
    if (isGroupAdmin() && groups?.data?.length) {
      const currentGroup = groups?.data?.find((g) => g.id === Number(groupId));
      setSelectedGroup(currentGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  return (
    <>
      {showModal && modalElement}
      {isSpinnerRunning() && <Spinner />}
      <Container fluid className="pb-3">
        <FiltersComponent
          stores={stores}
          spinner={spinner}
          dateState={dateState}
          allBrands={allBrands}
          setDateState={setDateState}
          selectedBrand={selectedBrand}
          selectedGroup={selectedGroup}
          allGroups={groups?.data ?? []}
          setSelectedBrand={setSelectedBrand}
          setSelectedGroup={setSelectedGroup}
          selectedLocation={selectedLocation}
          brandId={brandId || user?.brandId}
          setSelectedLocation={setSelectedLocation}
          getGroupsAgainstBrandId={getGroupsByBrandId}
          getStoresAgainstGroupId={getStoresAgainstGroupId}
          getStoresAgainstBrandId={getStoresAgainstBrandId}
          isTodayExcluded={true}
        />
        <div className="text-center pt-2 fw-bold">
          <h2>{displayName}</h2>
          <h5 className="mt-2">
            {MARKETING_ATTRIBUTION_LABELS.DATE_RANGE_PREFIX}{" "}
            {`${convertDateToMonthDayYearFormat(
              dateState.startDate
            )} - ${convertDateToMonthDayYearFormat(dateState.endDate)}`}
          </h5>
        </div>
        <MarketingAttributionChart
          chartData={chartData}
          totalOrders={
            processedMarketingAttributionReportPagination.totalElements
          }
          chartOptions={chartOptions}
          borderClassName="border border-secondary rounded p-1"
        />
        <MarketingAttributionReportTable
          testId="preMarketingAttributionResponseTable"
          columns={tableColumns}
          data={processedMarketingAttributionReportPaginatedData ?? []}
          totalRows={
            processedMarketingAttributionReportPagination?.totalElements
          }
          numberOfRowsPerPage={
            processedMarketingAttributionReportPagination?.pageSize
          }
          pageNumber={
            processedMarketingAttributionReportPagination?.pageNumber +
            PAGINATION_INDEXING.PAGE_INDEX_OFFSET
          }
          sortedBy={sorting}
          onPageChange={getDataOnPageChange}
          onColumnSort={setSorting}
        />
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  storesReducer: state.storesReducer,
  groupsReducer: state.groupsReducer,
  authenticationReducer: state.authentication,
  processedMarketingAttributionReportReducer:
    state.processedMarketingAttributionReportReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAllBrands: (isActiveOnly) =>
    dispatch(storeActions.getAllBrands(isActiveOnly)),
  getStoresAgainstBrandId: (brandId) =>
    dispatch(storeActions.getStoresAgainstBrandId(brandId)),
  getGroupsByBrandId: (brandId) => dispatch(groupsActions.getGroups(brandId)),
  getStoresAgainstGroupId: (groupId) =>
    dispatch(storeActions.getStoresAgainstGroupId(groupId)),
  getProcessedMarketingAttributionReportGraphData: (body) =>
    dispatch(
      processedMarketingAttributionReportActions.getProcessedMarketingAttributionReportGraphData(
        body
      )
    ),
  getProcessedMarketingAttributionReportPaginatedData: (body) =>
    dispatch(
      processedMarketingAttributionReportActions.getProcessedMarketingAttributionReportPaginatedData(
        body
      )
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProcessedMarketingAttributionReport);
